import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';

import { logChatbotLoadChatbot } from '../../bi';
import { useFedopsLogger } from '../../common/providers/fedopsLogger';
import { selectConversationId } from '../../common/selectors/chat';
import { selectEditorType } from '../../common/selectors/homepage';
import { selectIsCopilotInitialized, selectLoadPageUrl } from '../../common/selectors/meta';
import { selectSelectedSite } from '../../common/selectors/sites';
import { selectUserId } from '../../common/selectors/user';
import { selectWidgetSessionId } from '../../common/selectors/widgetSession';
import { loadChatbotStartChat } from '../../common/thunks/chatbotStartChat.thunk';
import { fetchSites } from '../../common/thunks/sites.thunk';
import { experiments } from '../../experiments';
import { widgetSDK } from '../../sdk/widgetSDK';
import { addWixRecorder } from '../../util/addWixRecorder';
import { renderModel } from '../../util/renderModel';

const isCopilot = renderModel.isCopilot();
const isPreChatSiteSelectorEnabled =
  experiments.is('specs.chatbot.FullPagePreChatSiteSelector', 'true') && !renderModel.isMobile();

const useAppLoaded = () => {
  const fedopsLogger = useFedopsLogger();
  const store = useStore();

  return useCallback(() => {
    logChatbotLoadChatbot(store.getState());
    fedopsLogger.appLoaded();
    const sessionId = selectWidgetSessionId(store.getState());
    const conversationId = selectConversationId(store.getState());
    addWixRecorder({ sessionId, conversationId });
  }, [fedopsLogger, store]);
};

export const useLoadApp = () => {
  const dispatch = useDispatch();
  const store = useStore();
  const appLoaded = useAppLoaded();
  const isCopilotInitialized = useSelector(selectIsCopilotInitialized);
  const loadPageUrl = useSelector(selectLoadPageUrl);
  const editorTypeFromRedux = useSelector(selectEditorType);

  const fedopsLogger = useFedopsLogger();
  const prevEditorType = useRef<string | undefined>(editorTypeFromRedux);

  useEffect(() => {
    if (isCopilot && !isCopilotInitialized) {
      return;
    }
    const loadApp = async () => {
      const userId = selectUserId(store.getState());
      const shouldFetchSites = isPreChatSiteSelectorEnabled && !!userId;
      const [context] = await Promise.all([
        widgetSDK.getContext(isCopilot ? loadPageUrl : undefined),
        shouldFetchSites ? dispatch(fetchSites(userId!)) : Promise.resolve(),
      ]);
      const editorType = selectEditorType(store.getState());
      const selectedSite = selectSelectedSite(store.getState());
      if (selectedSite?.id) {
        context.metaSiteId = selectedSite.id;
      }
      prevEditorType.current = editorType;
      await dispatch(loadChatbotStartChat({ context, editorType }));
      appLoaded();
    };
    fedopsLogger.appLoadStarted();
    void loadApp();
  }, [appLoaded, dispatch, fedopsLogger, isCopilotInitialized, loadPageUrl, store]);

  useEffect(() => {
    if (editorTypeFromRedux !== undefined && editorTypeFromRedux !== prevEditorType.current) {
      prevEditorType.current = editorTypeFromRedux;
      const reload = async () => {
        const context = await widgetSDK.getContext(isCopilot ? loadPageUrl : undefined);
        const selectedSite = selectSelectedSite(store.getState());
        if (selectedSite?.id) {
          context.metaSiteId = selectedSite.id;
        }
        await dispatch(loadChatbotStartChat({ context, editorType: editorTypeFromRedux }));
      };
      void reload();
    }
  }, [editorTypeFromRedux, dispatch, loadPageUrl]);
};
